import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Briefcase,
  Users,
  Shield,
  Eye,
  UserPlus,
  DollarSign,
  Target,
  Calendar,
  Activity,
  PlayCircle,
  CheckCircle,
  PauseCircle,
  FileText,
  Archive,
  LayoutGrid,
  List,
  Plus,
} from 'lucide-react';
import api from '../../lib/api';
import { Job } from '../../types';
import Select from '../../components/ui/Select';
import { formatDate } from '../../lib/utils';
import { jobUrlSegment } from '../../lib/slug';
import JobAssignmentModal from '../../components/JobAssignmentModal';
import CampaignPipelineChips from '../../components/CampaignPipelineChips';
import Button from '../../components/ui/Button';
import { useAuthStore } from '../../stores/authStore';

export default function CorporateJobs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [assignmentViewFilter, setAssignmentViewFilter] = useState('all');
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState<{ id: string; title: string } | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');

  useEffect(() => {
    loadJobs();
  }, [statusFilter]);

  const loadJobs = async () => {
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await api.get('/jobs', { params });
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG: Record<string, { icon: any; color: string; bg: string; border: string; label: string }> = {
    DRAFT: {
      icon: FileText,
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      label: 'Draft',
    },
    OPEN: {
      icon: PlayCircle,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      label: 'Open',
    },
    ACTIVE: {
      icon: Activity,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      label: 'Active',
    },
    PAUSED: {
      icon: PauseCircle,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      label: 'Paused',
    },
    COMPLETED: {
      icon: CheckCircle,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      label: 'Completed',
    },
    ARCHIVED: {
      icon: Archive,
      color: 'text-gray-500',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      label: 'Archived',
    },
  };

  const ROLE_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
    PRIMARY: { icon: Shield, color: 'text-purple-600', label: 'Primary' },
    COLLABORATOR: { icon: Users, color: 'text-blue-600', label: 'Collaborator' },
    REVIEWER: { icon: Eye, color: 'text-green-600', label: 'Reviewer' },
  };

  const canManageAssignments = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.roleInOrg || '');

  // Filter jobs based on assignment view
  const filteredJobs = jobs.filter((job: any) => {
    if (assignmentViewFilter === 'all') return true;
    if (assignmentViewFilter === 'my-jobs') {
      return job.assignments?.some((a: any) => a.userId === user?.id);
    }
    if (assignmentViewFilter === 'unassigned') {
      return !job.assignments || job.assignments.length === 0;
    }
    return true;
  });

  const getProgressPercentage = (current: number, target: number) => {
    if (!target || target === 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Campaigns</h1>
          <p className="text-glass-text-2 text-sm mt-1">View and manage your brand campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass-card flex items-center p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'card'
                  ? 'bg-accent2/15 text-accent2'
                  : 'text-glass-text-2 hover:text-glass-text hover:bg-glass-3'
              }`}
              title="Card View"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="text-sm font-medium">Cards</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-accent2/15 text-accent2'
                  : 'text-glass-text-2 hover:text-glass-text hover:bg-glass-3'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">List</span>
            </button>
          </div>
          <Button onClick={() => navigate('/brand/jobs/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create campaign
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Briefcase className="w-5 h-5 text-gray-400" />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-48"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="OPEN">Open</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="COMPLETED">Completed</option>
            <option value="ARCHIVED">Archived</option>
          </Select>
          
          <div className="h-6 w-px bg-gray-300"></div>
          
          <Users className="w-5 h-5 text-gray-400" />
          <Select
            value={assignmentViewFilter}
            onChange={(e) => setAssignmentViewFilter(e.target.value)}
            className="w-48"
          >
            <option value="all">All Campaigns</option>
            <option value="my-jobs">My Assigned Campaigns</option>
            <option value="unassigned">Unassigned Campaigns</option>
          </Select>
        </div>
      </div>

      {/* Jobs Display - Card or List View */}
      {filteredJobs.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Briefcase className="w-16 h-16 text-glass-text-3 mx-auto mb-4" />
          <h3 className="page-title text-lg mb-2">No campaigns found</h3>
          <p className="text-glass-text-2 mb-6">
            Create a draft campaign — your agency will publish it when it is ready to go live.
          </p>
          <Button onClick={() => navigate('/brand/jobs/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create campaign
          </Button>
        </div>
      ) : viewMode === 'card' ? (
        /* Card View */
        <div className="grid gap-4">{
          filteredJobs.map((job: any) => {
            const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.DRAFT;
            const StatusIcon = statusConfig.icon;
            const broadcastProgress = getProgressPercentage(job.broadcastedCount, job.estimatedKOL);
            const acceptanceRate = job.broadcastedCount > 0 
              ? Math.round((job.acceptedCount / job.broadcastedCount) * 100) 
              : 0;

            return (
              <div
                key={job.id}
                onClick={() => navigate(`/brand/jobs/${jobUrlSegment(job)}`)}
                className="glass-card hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150 cursor-pointer overflow-hidden group"
              >
                <div className="p-6">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.managedByOrg?.name || 'Agency'}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Status Badge and Action Button */}
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${statusConfig.border} ${statusConfig.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                        <span className={`text-sm font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>

                      {canManageAssignments && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobForAssignment({ id: job.id, title: job.title });
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span className="font-medium">Manage Team</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <CampaignPipelineChips
                      broadcastedCount={job.broadcastedCount}
                      acceptedCount={job.acceptedCount}
                      shortlistedCount={job.shortlistedCount}
                      approvedTalentCount={job.approvedTalentCount}
                    />
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    {/* Budget */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-600 uppercase">Budget</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {job.totalBudgetRM ? `RM ${job.totalBudgetRM.toLocaleString()}` : 'TBD'}
                      </div>
                    </div>

                    {/* Target KOLs */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-600 uppercase">Target</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {job.estimatedKOL || 0} KOLs
                      </div>
                    </div>

                    {/* Broadcasted */}
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="w-4 h-4 text-purple-600" />
                        <span className="text-xs font-medium text-purple-700 uppercase">Sent</span>
                      </div>
                      <div className="text-lg font-bold text-purple-900">
                        {job.broadcastedCount}
                      </div>
                    </div>

                    {/* Accepted */}
                    <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700 uppercase">Accepted</span>
                      </div>
                      <div className="text-lg font-bold text-green-900">
                        {job.acceptedCount}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bars */}
                  <div className="space-y-3 mb-4">
                    {/* Broadcast Progress */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-700">Broadcast Progress</span>
                        <span className="text-xs font-semibold text-gray-900">{broadcastProgress}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300"
                          style={{ width: `${broadcastProgress}%` }}
                        />
                      </div>
                    </div>

                    {/* Acceptance Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-gray-700">Acceptance Rate</span>
                        <span className="text-xs font-semibold text-gray-900">{acceptanceRate}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300"
                          style={{ width: `${acceptanceRate}%` }}
                        />
                      </div>
                    </div>

                    {/* Approved Content */}
                    {job.approvedTalentCount > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium text-gray-700">Approved Content</span>
                          <span className="text-xs font-semibold text-gray-900">{job.approvedTalentCount} ready</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                            style={{ width: `${getProgressPercentage(job.approvedTalentCount, job.acceptedCount)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Assigned Team */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-600 uppercase mb-2">Assigned Team</div>
                        {job.assignments && job.assignments.length > 0 ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {job.assignments.map((assignment: any) => {
                              const roleConfig = ROLE_CONFIG[assignment.role];
                              const RoleIcon = roleConfig.icon;
                              return (
                                <div
                                  key={assignment.id}
                                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-full px-3 py-1.5 hover:border-gray-300 transition-colors"
                                  title={`${roleConfig.label}: ${assignment.user.name}`}
                                >
                                  <RoleIcon className={`w-3.5 h-3.5 ${roleConfig.color}`} />
                                  <span className="text-xs font-medium text-gray-900">{assignment.user.name}</span>
                                  <span className={`text-xs ${roleConfig.color}`}>({roleConfig.label})</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Users className="w-4 h-4" />
                            <span>No team members assigned yet</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }</div>
      ) : (
        /* List View */
        <div className="glass-card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                {canManageAssignments && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredJobs.map((job: any) => {
                const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.DRAFT;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <tr
                    key={job.id}
                    onClick={() => navigate(`/brand/jobs/${jobUrlSegment(job)}`)}
                    className="hover:bg-glass-3/40 cursor-pointer transition-colors"
                  >
                    {/* Campaign */}
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{job.title}</div>
                      <div className="text-sm text-gray-500">{job.managedByOrg?.name || 'Agency'}</div>
                      <div className="mt-2">
                        <CampaignPipelineChips
                          broadcastedCount={job.broadcastedCount}
                          acceptedCount={job.acceptedCount}
                          shortlistedCount={job.shortlistedCount}
                          approvedTalentCount={job.approvedTalentCount}
                        />
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${statusConfig.border} ${statusConfig.bg}`}>
                        <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                        <span className={`text-xs font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </td>

                    {/* Team */}
                    <td className="px-6 py-4">
                      {job.assignments && job.assignments.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {job.assignments.slice(0, 2).map((assignment: any) => {
                            const roleConfig = ROLE_CONFIG[assignment.role];
                            const RoleIcon = roleConfig.icon;
                            return (
                              <div
                                key={assignment.id}
                                className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5"
                                title={`${assignment.user.name} - ${roleConfig.label}`}
                              >
                                <RoleIcon className={`w-3 h-3 ${roleConfig.color}`} />
                                <span className="text-xs text-gray-700">{assignment.user.name.split(' ')[0]}</span>
                              </div>
                            );
                          })}
                          {job.assignments.length > 2 && (
                            <span className="text-xs text-gray-500">+{job.assignments.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Unassigned</span>
                      )}
                    </td>

                    {/* Progress */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">{job.broadcastedCount}</span>
                          <span className="text-gray-500 text-xs ml-0.5">sent</span>
                        </div>
                        <div className="text-gray-300">•</div>
                        <div>
                          <span className="font-medium text-green-600">{job.acceptedCount}</span>
                          <span className="text-gray-500 text-xs ml-0.5">accepted</span>
                        </div>
                      </div>
                    </td>

                    {/* Budget */}
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {job.totalBudgetRM ? `RM ${job.totalBudgetRM.toLocaleString()}` : '-'}
                    </td>

                    {/* Target */}
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {job.estimatedKOL || '-'} KOLs
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(job.createdAt)}
                    </td>

                    {/* Actions */}
                    {canManageAssignments && (
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobForAssignment({ id: job.id, title: job.title });
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span>Manage</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Assignment Modal */}
      {selectedJobForAssignment && (
        <JobAssignmentModal
          jobId={selectedJobForAssignment.id}
          jobTitle={selectedJobForAssignment.title}
          onClose={() => setSelectedJobForAssignment(null)}
          onAssignmentChange={loadJobs}
        />
      )}
    </div>
  );
}
